import { getPageLanguage } from "../../../../react/shared/utils";
import { getSnowplowVariables, validateDate, validateDateTime, validateNumber } from "./utils";
import {
  validateContentMetadata,
  validateUiInteractionEvent,
  validateMatchContext,
  validateClubContext,
  validatePlayerContext,
  validateTopicContext
} from "./schemaValidation";

// Schema URIs - Events
export const UI_INTERACTION_SCHEMA = 'iglu:com.mlssoccer/ui_interaction_event/jsonschema/1-0-0';
export const LIKE_ACTION_SCHEMA = 'iglu:com.mlssoccer/like_context/jsonschema/1-0-0';
export const SHARE_ACTION_SCHEMA = 'iglu:com.mlssoccer/share_context/jsonschema/1-0-0';

// Schema URIs - Contexts
export const MATCH_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/match_context/jsonschema/1-0-0';
export const CLUB_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/club_context/jsonschema/1-0-0';
export const COMPETITION_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/competition_context/jsonschema/1-0-0';
export const PLAYER_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/player_context/jsonschema/1-0-0';
export const CAMPAIGN_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/campaign_context/jsonschema/1-0-0';
export const CHANNEL_CATEGORY_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/channel_category_context/jsonschema/1-0-0';
export const CONTENT_METADATA_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/content_metadata/jsonschema/1-0-0';
export const TOPIC_CONTEXT_SCHEMA = 'iglu:com.mlssoccer/topic_context/jsonschema/1-0-0';


/**
 * Creates the primary UI interaction event object.
 * @param {object} params - Parameters for the UI interaction event.
 * @param {string} params.element_name - Name or ID of the UI element.
 * @param {'click'|'tap'|'hover'|'scroll'|'swipe'|'drag'|'long_press'} params.interaction_type - Type of UI interaction.
 * @param {string} [params.element_type] - Type of UI element (e.g., button, image, link, card).
 * @param {string} [params.element_position] - Position of the element in the UI.
 * @returns {{schema: string, data: object}} The UI interaction event object.
 */
export function createUiInteractionPrimaryEvent({ element_name, interaction_type, element_type, element_position }) {
  const data = {
    element_name,
    interaction_type,
    element_type,
    element_position,
  };

  const validatedData = validateUiInteractionEvent(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: UI_INTERACTION_SCHEMA,
    data: validatedData,
  };
}

/**
 * Creates a match context object.
 * @param {object} params - Parameters for the match context.
 * @param {string} params.match_id - Sportec match ID.
 * @param {string} params.home_team - Name of the home team.
 * @param {string} params.away_team - Name of the away team.
 * @param {string} params.kickoff_time - Kickoff date and time in UTC (ISO 8601 format).
 * @param {string} [params.home_team_id] - Sportec ID for the home team.
 * @param {string} [params.away_team_id] - Sportec ID for the away team.
 * @param {string} [params.venue] - Name of the stadium or venue.
 * @param {'scheduled'|'live'|'completed'|'postponed'|'cancelled'} [params.match_status] - Current status of the match.
 * @returns {{schema: string, data: object}} The match context object.
 */
export function createMatchContext({ match_id, home_team, away_team, kickoff_time, home_team_id, away_team_id, venue, match_status } = {}) {
  if (!match_id || !home_team || !away_team || !kickoff_time) {
    return null;
  }

  const data = {
    match_id,
    home_team,
    away_team,
    kickoff_time: validateDateTime(kickoff_time),
    home_team_id,
    away_team_id,
    venue,
    match_status,
  };

  const validatedData = validateMatchContext(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: MATCH_CONTEXT_SCHEMA,
    data: validatedData,
  };
}

/**
 * Creates a club context object.
 * @param {object} params - Parameters for the club context.
 * @param {string} params.club_id - Sportec club ID.
 * @param {string} params.club_name - Name of club.
 * @param {string} [params.club_city] - Hometown of club.
 * @returns {{schema: string, data: object}} The club context object.
 */
export function createClubContext({ club_id = null, club_name = null, club_city = null } = {}) {
  if (!club_id || !club_name) {
    return null;
  }

  const data = {
    club_id,
    club_name,
    club_city,
  }

  const validatedData = validateClubContext(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: CLUB_CONTEXT_SCHEMA,
    data: validatedData,
  };
}

/**
 * Creates a competition context object.
 * @param {object} params - Parameters for the competition context.
 * @param {string} params.competition_id - Sportec Competition ID.
 * @param {string} params.competition_name - Name of the competition.
 * @param {string} params.season - Sportec Season ID.
 * @param {string} [params.start_date] - Start date of the competition (ISO 8601 format YYYY-MM-DD).
 * @param {string} [params.end_date] - End date of the competition (ISO 8601 format YYYY-MM-DD).
 * @param {number} [params.number_of_teams] - Total number of participating teams.
 * @returns {{schema: string, data: object}} The competition context object.
 */
export function createCompetitionContext({ competition_id = null, competition_name = null, season = null, start_date = null, end_date = null, number_of_teams = null } = {}) {
  if (!competition_id || !competition_name || !season) {
    return null;
  }


  return {
    schema: COMPETITION_CONTEXT_SCHEMA,
    data: {
      competition_id,
      competition_name,
      season: (season || phase).toString(),
      start_date: validateDate(start_date),
      end_date: validateDate(end_date),
      number_of_teams: validateNumber(number_of_teams),
    },
  };
}

/**
 * Creates a player context object.
 * @param {object} params - Parameters for the player context.
 * @param {string} params.player_id - Sportec player ID.
 * @param {string} params.player_name - Name of the player.
 * @param {string} [params.player_team] - Playing club.
 * @param {string} [params.player_position] - Player's position on the field.
 * @param {number} [params.jersey_number] - Player's jersey number.
 * @returns {{schema: string, data: object}} The player context object.
 */
export function createPlayerContext({ player_id = null, player_name = null, player_team = null, player_position = null, jersey_number = null } = {}) {
  if (!player_id || !player_name) {
    return null;
  }

  const data = {
    player_id,
    player_name,
    player_team,
    player_position,
    jersey_number: validateNumber(jersey_number),
  }

  const validatedData = validatePlayerContext(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: PLAYER_CONTEXT_SCHEMA,
    data: validatedData,
  };
}

/**
 * Creates a campaign context object.
 * @param {object} params - Parameters for the campaign context.
 * @param {string} params.mls_campaign_id - MLS campaign identifier.
 * @param {string} [params.sponsor] - Sponsor of the campaign.
 * @returns {{schema: string, data: object}} The campaign context object.
 */
export function createCampaignContext({ mls_campaign_id = null, sponsor = null } = {}) {
  if (!mls_campaign_id) {
    return null;
  }

  return {
    schema: CAMPAIGN_CONTEXT_SCHEMA,
    data: {
      mls_campaign_id,
      sponsor,
    },
  };
}

/**
 * Creates a channel category context object.
 * @param {object} params - Parameters for the channel category context.
 * @param {string} params.channel_id - Identifier for the channel.
 * @param {string} params.category_id - Identifier for the category.
 * @param {string} [params.channel_name] - Name of the channel.
 * @param {string} [params.category] - Category name.
 * @param {string} [params.view_type] - Type of view.
 * @returns {{schema: string, data: object}} The channel category context object.
 */
export function createChannelCategoryContext({ channel_id = null, category_id = null, channel_name = null, category = null, view_type = null } = {}) {
  if (!channel_id || !category_id) {
    return null;
  }

  return {
    schema: CHANNEL_CATEGORY_CONTEXT_SCHEMA,
    data: {
      channel_id,
      category_id,
      channel_name,
      category,
      view_type,
    },
  };
}

/**
 * Creates a content metadata context object.
 * @param {object} params - Parameters for the content metadata context.
 * @param {string} params.content_id - Unique identifier for the content.
 * @param {string} params.content_title - Title of the content.
 * @param {string} params.slug - URL slug for the content.
 * @param {string} [params.content_format] - Format of the content.
 * @param {string} [params.content_type] - Type of content.
 * @param {string} [params.embedded_content_id] - ID of embedded content.
 * @param {string} [params.asset_type] - Type of asset.
 * @param {number} [params.word_count] - Number of words in the content.
 * @param {number} [params.duration_time] - Duration of content in seconds.
 * @param {string} [params.publish_date] - Publication date (YYYY-MM-DD).
 * @param {string} [params.topic_primary] - Primary topic.
 * @param {string} [params.topic_secondary] - Secondary topic.
 * @param {string} [params.language] - Language of the content.
 * @param {string} [params.author] - Author of the content.
 * @returns {{schema: string, data: object}} The content metadata context object.
 */
export function createContentMetadataContext({
  content_id = null,
  content_title = null,
  slug = null,
  content_format = null,
  content_type = null,
  embedded_content_id = null,
  asset_type = null,
  word_count = null,
  duration_time = null,
  publish_date = null,
  topic_primary = null,
  topic_secondary = null,
  language = null,
  author = null,
} = {}) {
  if (!content_id || !content_title || !slug) {
    return null;
  }

  const data = {
    content_id,
    content_title,
    slug,
    content_format,
    content_type,
    embedded_content_id,
    asset_type,
    word_count: validateNumber(word_count),
    duration_time: validateNumber(duration_time),
    publish_date: validateDate(publish_date),
    topic_primary,
    topic_secondary,
    language,
    author,
  };

  const validatedData = validateContentMetadata(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: CONTENT_METADATA_CONTEXT_SCHEMA,
    data: validatedData,
  };
}

/**
 * Creates a like action event object.
 * @param {object} params - Parameters for the like action event.
 * @param {string} params.like_type - Type of like action.
 * @param {string} params.content_id - ID of the content being liked.
 * @returns {{schema: string, data: object}} The like action event object.
 */
export function createLikeActionEvent({ like_type = null, content_id = null } = {}) {
  if (!like_type || !content_id) {
    return null;
  }

  return {
    schema: LIKE_ACTION_SCHEMA,
    data: {
      like_type,
      content_id,
    },
  };
}

/**
 * Creates a share action event object.
 * @param {object} params - Parameters for the share action event.
 * @param {string} params.shared_with - Platform where content is shared.
 * @param {string} params.content_id - ID of the content being shared.
 * @returns {{schema: string, data: object}} The share action event object.
 */
export function createShareActionEvent({ shared_with = null, content_id = null } = {}) {
  if (!shared_with || !content_id) {
    return null;
  }

  return {
    schema: SHARE_ACTION_SCHEMA,
    data: {
      shared_with,
      content_id,
    },
  };
}

/**
 * Creates a topic context object.
 * @param {object} params - Parameters for the topic context.
 * @param {string} params.topic_id - Unique identifier for the topic.
 * @param {string} params.topic_name - Name of the topic.
 * @param {string} params.topic_slug - URL slug for the topic.
 * @param {string} params.topic_type - Type of the topic.
 * @returns {{schema: string, data: object}} The topic context object.
 */
export function createTopicContext({ topic_id = null, topic_name = null, topic_slug = null, topic_type = null } = {}) {
  if (!topic_id || !topic_name || !topic_slug || !topic_type) {
    return null;
  }

  const data = {
    topic_id,
    topic_name,
    topic_slug,
    topic_type,
  };

  const validatedData = validateTopicContext(data);
  if (!validatedData) {
    return null;
  }

  return {
    schema: TOPIC_CONTEXT_SCHEMA,
    data: validatedData,
  };
}


// Compose Context Data Functions

// The mlsMatch object should conform to the MLS Match Schema structure.
// https://stats-api-spec.mlsdev.net/#tag/Matches/operation/getMatch
export function composeMLSMatchContextData({ away = null, home = null, environment = null, match_information = null } = {}) {
  return {
    venue: environment?.stadium_name,
    match_id: match_information.match_id,
    home_team: home?.team_name || home?.team_short_name || home?.team_three_letter_code,
    away_team: away?.team_name || away?.team_short_name || away?.team_three_letter_code,
    kickoff_time: validateDateTime(match_information?.kickoff_time),
    match_status: match_information?.match_status,
    home_team_id: home?.team_id,
    away_team_id: away?.team_id,
  }
}

export function composeMLSClubContextData({ team_id = null, team_name = null, team_city = null } = {}) {
  return {
    club_id: team_id,
    club_name: team_name,
    club_city: team_city,
  }
}

export function composeMLSCompetitionContextData({ competition_id = null, competition_name = null, season = null, start_date = null, end_date = null, number_of_teams = null } = {}) {
  return {
    competition_id,
    competition_name,
    season,
    start_date: validateDate(start_date),
    end_date: validateDate(end_date),
    number_of_teams: validateNumber(number_of_teams),
  }
}

export function composeChannelCategoryContextData() {
  const { channelName, category, pageType } = getSnowplowVariables()

  return createChannelCategoryContext({
    channel_id: channelName,
    channel_name: channelName,
    category_id: category,
    category: category,
    view_type: pageType,
  });
}

export function composeContentMetadataContextData({
  id = null, title = null, slug = null, format = null, type = null, assetType = null, wordCount = null, durationTime = null, publishDate = null,
  topicPrimary = null, topicSecondary = null, language = null, author = null,
} = {}) {

  if (!id || !title || !slug) {
    return null;
  }

  return {
    content_id: id,
    content_title: title,
    slug,
    content_format: format,
    content_type: type,
    asset_type: assetType,
    word_count: validateNumber(wordCount),
    duration_time: validateNumber(durationTime),
    publish_date: validateDate(publishDate),
    topic_primary: topicPrimary,
    topic_secondary: topicSecondary,
    language,
    author,
  }
}

export function composeTopicContextData({ id = null, name = null, slug = null, type = null } = {}) {
  if (!id || !name || !slug || !type) {
    return null;
  }

  return {
    topic_id: id,
    topic_name: name,
    topic_slug: slug,
    topic_type: type,
  };
}

// Dynamic Schema Context Functions

export function getDynamicArticleData() {
  const data = window.forgeVariables.tracking?.article;

  if (!data) {
    return undefined;
  }

  return {
    content_id: data.id,
    content_title: data.title,
    slug: data.slug,
    content_type: data.type,
    publish_date: validateDate(data.contentDate),
    author: data.createdBy,
    topic_primary: data.topicPrimary,
    topic_secondary: data.topicSecondary,
    language: getPageLanguage(),
  };
}

export function getDynamicPlayerData() {
  const data = window.forgeVariables.tracking?.player;

  if (!data) {
    return undefined;
  }

  return {
    player_id: data.player_id,
    player_name: data.player_name,
  };
}

export function getDynamicClubData() {
  const data = window.forgeVariables.tracking?.club;

  if (!data) {
    return undefined;
  }

  return {
    club_id: data.club_id,
    club_name: data.club_name,
  };
}

export function getDynamicVideoPlaylistData() {
  const data = window.forgeVariables.tracking?.videoPlaylist;

  if (!data && data.length > 0) {
    return undefined;
  }

  return {
    content_id: data._entityId,
    content_title: data.title,
    slug: data.slug,
    content_type: data.type,
    content_format: "video",
    asset_type: "video",
    duration_time: validateNumber(data?.fields?.duration),
    publish_date: validateDate(data.contentDate),
    author: data.createdBy,
    topic_primary: data.tags[0]?.title,
    topic_secondary: data.tags[1]?.title,
    language: getPageLanguage(),
  };
}
